import {
	H2,
	HStack,
	Input,
	Select,
	SelectOptionType,
} from '@montel/montelpro-shared-components-v2';
import useSubmit, { POST } from 'hooks/useSubmit';
import { FunctionComponent, useState } from 'react';
import { FormContainer, StyledButton } from './InviteAdminForm.styles';
import { RequestStatus } from '@montel/montelpro-shared-components/enums';
import { ADMIN_ACCOUNT_API_ROUTE } from 'apiRoutes';
import { useAdminUsersContext } from '../..';
import { toRoleOptions } from '../../toRoleOptions';
import { isEmail } from 'utils/isEmail';
import { initialFormState } from './initialFormState';
import { AdminAccount } from 'domain/account/interfaces/AdminAccount';
import { AccessControl } from 'components/common/AccessControl/AccessControl';
import { AdminRole } from 'domain/account/enums/AdminRole';

interface IFormState {
	firstName: string;
	lastName: string;
	email: string;
	roles: SelectOptionType[] | undefined;
}

export const InviteAdminForm: FunctionComponent<{
	onSuccess: (createdAdmin: AdminAccount) => void;
}> = ({ onSuccess }) => {
	const { adminRoles } = useAdminUsersContext();
	const [formState, setFormState] = useState<IFormState>(initialFormState);
	const [errors, setErrors] = useState({
		email: '',
		roles: '',
		firstName: '',
		lastName: '',
	});

	const [submit, status] = useSubmit(
		`${ADMIN_ACCOUNT_API_ROUTE}/CreateAdmin`,
		POST,
		{
			messageOnSuccess: 'Admin invited successfully',
			messageOnError: 'Error inviting admin',
			showApiError: true,
		},
	);

	const onSubmit = async () => {
		if (!isEmail(formState.email)) {
			setErrors({
				...errors,
				email: 'A valid email is required',
			});

			return;
		}

		const response = await submit({
			...formState,
			roles: formState.roles?.map((role) => role.value),
		});
		if (response.success) {
			onSuccess(response.data);
			setFormState(initialFormState);
		}
	};

	const hasErrors = () =>
		Boolean(
			errors.email || errors.roles || errors.firstName || errors.lastName,
		);

	const hasEmptyFields = () =>
		!formState.email ||
		!formState.firstName ||
		!formState.lastName ||
		!formState.roles?.length;

	return (
		<FormContainer fullWidth gap="md">
			<H2>Invite Admin User</H2>
			<HStack wrap="nowrap" fullWidth>
				<Input
					label="First Name"
					placeholder="First Name"
					autoComplete="off"
					name="firstName"
					value={formState.firstName}
					required
					onChange={(event) =>
						setFormState({
							...formState,
							firstName: event.target.value,
						})
					}
					errorMessage={errors.firstName && errors.firstName}
				/>
				<Input
					label="Last Name"
					placeholder="Last Name"
					autoComplete="off"
					name="lastName"
					value={formState.lastName}
					required
					onChange={(event) =>
						setFormState({
							...formState,
							lastName: event.target.value,
						})
					}
					errorMessage={errors.lastName && errors.lastName}
				/>
			</HStack>
			<HStack wrap="nowrap" fullWidth>
				<Input
					label="Email"
					placeholder="Email"
					autoComplete="off"
					name="email"
					value={formState.email}
					required
					onChange={(event) => {
						setFormState({
							...formState,
							email: event.target.value,
						});
						setErrors({
							...errors,
							email: '',
						});
					}}
					errorMessage={errors.email && errors.email}
				/>
				<Select
					label="Roles"
					name="roles"
					value={formState.roles}
					onChange={(selected) =>
						setFormState({
							...formState,
							roles: selected,
						})
					}
					required
					options={toRoleOptions(adminRoles)}
					isMulti
					variant="checkbox"
				/>
			</HStack>
			<AccessControl allowedRoles={[AdminRole.SUPER_ADMIN]}>
				<StyledButton
					onClick={onSubmit}
					type="submit"
					text="Invite"
					loadingText="Inviting user ..."
					isDisabled={hasErrors() || hasEmptyFields()}
					isLoading={status === RequestStatus.LOADING}
				/>
			</AccessControl>
		</FormContainer>
	);
};
